import { MobXProviderContext } from "mobx-react";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { ProgressSpinner } from "primereact/progressspinner";
import React, { useContext, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { getErrorTitle } from "../../../constants/constants";
import { RouterAddress } from "../../../constants/RouterAddress";
import { appAccentPanelStyle, appMutedTextStyle, appNeutralPanelStyle, renderSurfaceHeader } from "../../../components/general/uiSurfaceStyles";
import { useAuthExperience } from "../../auth/hooks/useAuthExperience";

const EmailPreferences = () => {
  const { backendAPIStore } = useContext(MobXProviderContext);
  const authExperience = useAuthExperience();
  const [searchParams] = useSearchParams();
  const token = String(searchParams.get("token") || "").trim();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [preference, setPreference] = useState<any | null>(null);

  const loadPreference = async () => {
    if (!token) {
      setErrorMessage("This email preference link is missing a token.");
      setLoading(false);
      return;
    }

    setLoading(true);
    setErrorMessage("");
    try {
      const response = await backendAPIStore.getCampaignEmailPreference(token);
      setPreference(response || null);
    } catch (error: any) {
      setErrorMessage(error?.message || "Unable to load this email preference.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadPreference();
  }, [token]);

  const updatePreference = async (subscribe: boolean) => {
    setSaving(true);
    setErrorMessage("");
    try {
      const response = await backendAPIStore.updateCampaignEmailPreference({
        token,
        subscribe,
      });
      setPreference(response || null);
    } catch (error: any) {
      setErrorMessage(error?.message || "Unable to update this email preference.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen flex align-items-center justify-content-center px-3 py-6" style={{ background: "linear-gradient(180deg, #fff7ed 0%, #fffbeb 100%)" }}>
      <Card style={{ width: "100%", maxWidth: "42rem", borderRadius: "24px", boxShadow: "0 24px 60px rgba(148, 64, 12, 0.12)" }}>
        <div className="flex flex-column gap-4">
          <div>
            {renderSurfaceHeader("Communication Preferences", `Manage your ${authExperience.brandName} admin campaign email subscription.`, "pi pi-envelope")}
          </div>

          {loading ? (
            <div className="flex justify-content-center py-6">
              <ProgressSpinner style={{ width: "54px", height: "54px" }} strokeWidth="4" />
            </div>
          ) : errorMessage ? (
            <div style={{ ...appAccentPanelStyle, color: "#7c2d12" }}>
              <div style={{ fontWeight: 700, marginBottom: "0.4rem" }}>{getErrorTitle({ message: errorMessage })}</div>
              <div style={appMutedTextStyle}>{errorMessage}</div>
            </div>
          ) : (
            <>
              <div style={appNeutralPanelStyle}>
                <div style={{ fontWeight: 700, color: "#0f172a" }}>Account</div>
                <div className="mt-2" style={appMutedTextStyle}>
                  <strong>Email:</strong> {preference?.email || "Unknown"}
                </div>
                {preference?.roleType ? (
                  <div className="mt-2" style={appMutedTextStyle}>
                    <strong>Role:</strong> {preference.roleType}
                  </div>
                ) : null}
                <div className="mt-2" style={appMutedTextStyle}>
                  <strong>Status:</strong> {preference?.emailCampaignSubscribed ? "Subscribed to admin campaign emails" : "Unsubscribed from admin campaign emails"}
                </div>
                {preference?.updatedAt ? (
                  <div className="mt-2" style={appMutedTextStyle}>
                    <strong>Updated:</strong> {new Date(preference.updatedAt).toLocaleString()}
                  </div>
                ) : null}
              </div>

              <div style={preference?.emailCampaignSubscribed ? appAccentPanelStyle : appNeutralPanelStyle}>
                <div style={{ fontWeight: 700, color: "#9a3412" }}>
                  {preference?.emailCampaignSubscribed ? "You are subscribed" : "You are unsubscribed"}
                </div>
                <div className="mt-2" style={appMutedTextStyle}>
                  This preference only affects admin campaign emails sent from {authExperience.brandName}. Transactional account, billing, and workflow emails are not changed here.
                </div>
              </div>

              <div className="flex flex-wrap gap-3">
                {preference?.emailCampaignSubscribed ? (
                  <Button
                    label="Unsubscribe"
                    severity="warning"
                    rounded
                    loading={saving}
                    onClick={() => void updatePreference(false)}
                  />
                ) : (
                  <Button
                    label="Subscribe Again"
                    severity="warning"
                    rounded
                    loading={saving}
                    onClick={() => void updatePreference(true)}
                  />
                )}
                <Button
                  label="Back to Home"
                  severity="secondary"
                  outlined
                  rounded
                  onClick={() => { window.location.href = RouterAddress.APP_ROOT; }}
                />
              </div>
            </>
          )}
        </div>
      </Card>
    </div>
  );
};

export default EmailPreferences;
